import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { ColorDifference } from "components/colorSpecificationList/ColorDifference";
import { useContrastBorder } from "hooks/contrast-border";
import type { Dispatch, FC } from "react";
import { FormatColor, formatColorDifference } from "service/color-formatting";
import { labToCss } from "service/color-math";
import type { NamedColor } from "../filter/color-filter/ColorFilter";

export interface ColorComparisonDialogProps {
  preview: NamedColor;
  target: NamedColor;
  open: boolean;
  onClose: Dispatch<string>;
}

export const ColorComparisonDialog: FC<ColorComparisonDialogProps> = ({
  preview,
  target,
  open,
  onClose,
}) => {
  const contrastBorder = useContrastBorder(preview.value, target.value);

  const handleClose = (_e: {}, reason: "backdropClick" | "escapeKeyDown") =>
    onClose(reason);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Color Comparison</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            Colors and color differences displayed on screen differ from
            reality.
          </Alert>
          <TableContainer>
            <Table
              size="small"
              sx={{
                "tr td": { fontSize: "var(--body-b1-16-regular-size, 16px)" },
                "td, th": { borderBottom: 0 },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      height: 150,
                      width: 300,
                      border: contrastBorder,
                      borderRight: "none !important",
                      borderBottom: `${contrastBorder} !important`,
                      backgroundColor: labToCss(target.value),
                    }}
                  ></TableCell>
                  <TableCell
                    sx={{
                      height: 150,
                      width: 300,
                      border: contrastBorder,
                      borderLeft: "none !important",
                      borderBottom: `${contrastBorder} !important`,
                      backgroundColor: labToCss(preview.value),
                    }}
                  ></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align={"center"}>
                    <Typography
                      fontSize="var(--body-b3-12-regular-size, 12px)"
                      color="text.secondary"
                    >
                      Your Search
                    </Typography>
                  </TableCell>
                  <TableCell align={"center"}>
                    <Typography
                      fontSize="var(--body-b3-12-regular-size, 12px)"
                      color="text.secondary"
                    >
                      Result
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align={"center"}>
                    <Typography variant="label">{target?.name}</Typography>
                  </TableCell>
                  <TableCell align={"center"}>
                    <Typography variant="label">{preview.name}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align={"center"}>
                    <FormatColor color={target?.value} />
                    <FormHelperText sx={{ textAlign: "center" }}>
                      {target.measurementCondition}
                    </FormHelperText>
                  </TableCell>
                  <TableCell align={"center"}>
                    <FormatColor color={preview.value} />
                    <FormHelperText sx={{ textAlign: "center" }}>
                      {preview.measurementCondition}
                    </FormHelperText>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack alignItems="center" pt={1} spacing={0.5}>
            <FormHelperText sx={{ textAlign: "center" }}>
              Color Difference
            </FormHelperText>
            <Typography>
              <Typography display="inline-block" variant="label">
                ΔE*<sub>00</sub>
              </Typography>
              &nbsp;
              {formatColorDifference(preview?.deltaE ?? 0)}
            </Typography>
            <ColorDifference
              width="auto"
              includeDeltaE={false}
              value={preview.value}
              target={target?.value}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={(_e) => onClose("buttonClick")}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
